import { getSupabase } from '@/lib/supabaseClient';

class NotificationService {
  constructor() {
    this.supabase = getSupabase();
  }

  async getUserPreferences(userId) {
    const { data: profile, error } = await this.supabase
      .from('profiles')
      .select('notification_preferences')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return profile?.notification_preferences || {};
  }

  async getNotificationTemplate(type) {
    const { data: template, error } = await this.supabase
      .from('notification_templates')
      .select('*')
      .eq('type', type)
      .single();

    if (error) throw error;
    return template;
  }

  async sendNotification(userId, type, variables = {}) {
    try {
      const preferences = await this.getUserPreferences(userId);
      const template = await this.getNotificationTemplate(type);

      if (!template) {
        throw new Error(`Notification template not found for type: ${type}`);
      }

      const notifications = [];

      // Send in-app notification
      if (preferences.in_app) {
        const inAppNotification = await this.sendInAppNotification(userId, template.in_app_message, variables);
        notifications.push(inAppNotification);
      }

      // Send email notification
      if (preferences.email) {
        const emailNotification = await this.sendEmailNotification(
          userId,
          this.replaceVariables(template.email_subject, variables),
          this.replaceVariables(template.email_body, variables)
        );
        notifications.push(emailNotification);
      }

      // Send push notification
      if (preferences.push) {
        const pushNotification = await this.sendPushNotification(
          userId,
          this.replaceVariables(template.push_title, variables),
          this.replaceVariables(template.push_body, variables)
        );
        notifications.push(pushNotification);
      }

      return notifications;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  }

  async sendInAppNotification(userId, message, variables = {}) {
    console.log('sendInAppNotification called:', { userId, message, variables });
    const notificationData = {
      user_id: userId,
      type: 'last_login',
      message: this.replaceVariables(message, variables),
      read_status: false
    };
    console.log('Inserting notification:', notificationData);
    
    const { data, error } = await this.supabase
      .from('notifications')
      .insert(notificationData)
      .select()
      .single();

    if (error) {
      console.error('Error inserting notification:', error);
      throw error;
    }
    console.log('Notification inserted successfully:', data);
    return data;
  }

  async sendEmailNotification(userId, subject, body) {
    const { data: user, error: userError } = await this.supabase
      .from('auth.users')
      .select('email')
      .eq('id', userId)
      .single();

    if (userError) throw userError;

    // TODO: Integrate with email service (e.g., SendGrid, AWS SES)
    return {
      type: 'email',
      status: 'sent',
      recipient: user.email
    };
  }

  async sendPushNotification(userId, title, body) {
    const { data: devices, error: devicesError } = await this.supabase
      .from('user_devices')
      .select('push_token, device_type')
      .eq('user_id', userId);

    if (devicesError) throw devicesError;

    // TODO: Integrate with push notification service (e.g., Firebase Cloud Messaging)
    return {
      type: 'push',
      status: 'sent',
      devices: devices.map(d => d.device_type)
    };
  }

  replaceVariables(text, variables) {
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => variables[key] || match);
  }

  async registerDevice(userId, pushToken, deviceType) {
    const { data, error } = await this.supabase
      .from('user_devices')
      .upsert({
        user_id: userId,
        push_token: pushToken,
        device_type: deviceType,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async unregisterDevice(userId, pushToken) {
    const { error } = await this.supabase
      .from('user_devices')
      .delete()
      .eq('user_id', userId)
      .eq('push_token', pushToken);

    if (error) throw error;
  }

  async updateNotificationPreferences(userId, preferences) {
    const { data, error } = await this.supabase
      .from('profiles')
      .update({ notification_preferences: preferences })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async sendLastLoginNotification(userId, lastSignInAt) {
    console.log('sendLastLoginNotification called:', { userId, lastSignInAt });
    let message;
    
    if (!lastSignInAt) {
      // No previous login timestamp means this is the first login ever
      message = `Welcome to UrbanMint! This is your first login. Let's start building your financial future together! 🚀`;
      console.log('First login message:', message);
    } else {
      // Has previous login - show welcome back message
      const date = new Date(lastSignInAt);
      const timeDiff = Date.now() - date.getTime();
      const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 0) {
        message = `Welcome back! You logged in earlier today.`;
      } else if (daysDiff === 1) {
        message = `Welcome back! Your last login was yesterday.`;
      } else if (daysDiff < 7) {
        message = `Welcome back! Your last login was ${daysDiff} days ago.`;
      } else if (daysDiff < 30) {
        const weeksDiff = Math.floor(daysDiff / 7);
        message = `Welcome back! Your last login was ${weeksDiff} week${weeksDiff > 1 ? 's' : ''} ago.`;
      } else {
        message = `Welcome back! Your last login was on ${date.toLocaleDateString()}.`;
      }
      console.log('Returning user message:', message, { daysDiff, lastSignInAt });
    }
    
    const result = await this.sendInAppNotification(userId, message);
    console.log('sendInAppNotification result:', result);
    return result;
  }
}

export const notificationService = new NotificationService(); 